import { useCallback, useContext, useMemo } from 'react';
import { clone, flatten, get, isEqual, toPath } from 'lodash';
import useGetter from '@dyl/hooks/useGetter';
import TaskContext from './TaskContext';
import sentenceCase from '@dyl/utils/sentenceCase';

/**
 * @template T
 * @typedef {[T, React.Dispatch<React.SetStateAction<T>>, string]} UseTaskField
 */

/**
 * Provides value and setter (like `useState()`), and API validation error,
 *  for the value at the given path.
 * @template T
 * @param {string} fieldName
 * @returns {UseTaskField<T>} [value, setValue, error]
 */
export default function useTaskField(...pathParts) {
    const {
        data: task,
        taskType,
        setData: setTask,
        apiValidationErrorData,
    } = useContext(TaskContext);

    const getPath = useGetter(toPath(flatten(pathParts)));
    const path = getPath();
    const value = path.length > 0 ? get(task, getPath()) : task;

    const setValue = useCallback(nextValue => {
        setTask(task => {
            const parents = getPath();
            const field = parents.pop();

            // First create a shallow clone of the root
            task = clone(task);

            // For each parent, grandparent...
            let currentCollection = task;
            const path = getPath();
            for (const subPath of path) {
                // Shallow clone, so React recognizes changes
                currentCollection[subPath] = clone(currentCollection[subPath]);
                // Create sub-path in case it does not exist
                if (!currentCollection[subPath])
                    Object.assign(currentCollection, { [subPath]: {}});
                // Update currentCollection for the following operation to use
                currentCollection = currentCollection[subPath];
            }

            // Set the nested value either directly, or using a callback
            if (field === undefined) {
                task = (typeof nextValue === 'function' ? nextValue(currentCollection) : nextValue);
            }
            currentCollection[field] = (
                typeof nextValue === 'function'
                    ? nextValue(currentCollection[field]) : nextValue
            );
            return task;
        });
    }, []);

    const apiError = useMemo(() => {
        const obj = apiValidationErrorData.find(d => isPathEqual(d.path, getPath()));

        if (!obj) return null;

        let { message } = obj;

        // Remove preceding mention of the path and 'is'/'with value'.
        message = message.replace(/^"[^"]+"\s*(?:(?:is|with\s+value)\s+)?/, '');

        // Reword re string type, the latter makes more sense to the user.
        message = message.replace('must be a string', 'not allowed to be empty');

        message = sentenceCase(message);

        return message;
    }, [apiValidationErrorData]);

    const erroneousValue = useMemo(() => value, [apiError]);

    const shouldShowError = !!apiError && value === erroneousValue;

    return [
        value,
        setValue,
        shouldShowError ? apiError : null,
        taskType
    ];
}

/**
 * @param {(string | number)[]} a
 * @param {(string | number)[]} b
 */
function isPathEqual(a, b) {
    a = a.map(v => String(v));
    b = b.map(v => String(v));

    return isEqual(a, b);
}